import { DOCUMENT, registerLocaleData } from '@angular/common';
import { ApplicationRef, Inject, Injectable, Injector, LOCALE_ID, NgZone, Optional } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { initializeApi } from '@spa/api';
import { SpaApiEvents } from '@spa/api/events';
import { IsDev } from '@spa/common/services/is-dev';
import { OpenNewsService } from '@spa/common/services/open-news.service';
import { OpenPortalService } from '@spa/common/services/open-portal.service';
import { TabActiveStateService } from '@spa/common/services/tab-active-state.service';
import { MtfIframeService } from '@spa/common/services/mtf-iframes.service';
import { UserSettingsFacadeProvider } from '@spa/facade/features/user-settings';
import {
	AbstractLogger,
	AppBootstrapEvent,
	AppVersionService,
	ASSET_INVALIDATE_KEY,
	AuthService,
	ConfigurationProvider,
	CookieService,
	EventBusService,
	LoggerFactory,
	PlatformDetectorProvider,
	SessionUser,
	SystemCookies,
	UrlProvider,
	WINDOW,
} from '@valhalla/core';
import { SignalrProvider } from '@valhalla/data/signalr';
import { CultureService } from '@valhalla/localization';
import { booleanFilter } from '@valhalla/utils';
import { EMPTY, fromEvent, interval, merge, Observable } from 'rxjs';
import {
	catchError,
	concatMap,
	distinctUntilChanged,
	exhaustMap,
	filter,
	mergeMap,
	skip,
	switchMap,
	take,
	tap,
} from 'rxjs/operators';
import * as packageJson from '../../../../../package.json';
import { initializeGlobalEventHandlers } from '../global-event-handlers/initialize';
import { UnsaveChangesService } from '../guards/unsave-changes.service';
import { LazyLocale, LazyLocaleType } from './lazy-ng-locale';
import { formaIcons } from './register-icons';
import { patch } from './patch';

/**
 * Service has one method 'run()' called after bootstrap application
 */
@Injectable()
export class PostBootstrapService {
	private _bootstrapped = false;
	private readonly _logger: AbstractLogger;

	constructor(
		private readonly _events: EventBusService,
		private readonly _platformDetector: PlatformDetectorProvider,
		loggerService: LoggerFactory,
		@Inject(WINDOW) private readonly _window: Window,
		@Inject(DOCUMENT) private readonly _document: Document,
		private readonly _auth: AuthService,
		private readonly _config: ConfigurationProvider,
		@Optional() private readonly _animationModule: BrowserAnimationsModule,
		private readonly _cookies: CookieService,
		protected zone: NgZone,
		readonly versionService: AppVersionService,
		readonly iconRegistry: MatIconRegistry,
		readonly sanitizer: DomSanitizer,
		readonly url: UrlProvider,
		readonly injector: Injector,
		@Inject(LazyLocale) readonly lazyLocaleNgModule: LazyLocaleType,
		@Inject(LOCALE_ID) readonly localeId: string,
		readonly apiEvents: SpaApiEvents,
		readonly signal: SignalrProvider,
		readonly openNewsService: OpenNewsService,
		readonly openPortalService: OpenPortalService,
		readonly sessionUserSettings: UserSettingsFacadeProvider,
		readonly sessionUser: SessionUser,
		readonly mtfFrame: MtfIframeService,
		readonly unsavedChanges: UnsaveChangesService,
		readonly culture: CultureService,
		@Inject(IsDev) readonly isDev: boolean,
		@Inject(ASSET_INVALIDATE_KEY) readonly ASSET_INVALIDATE_KEY: string,
		readonly tabActiveStateService: TabActiveStateService,
		readonly appRef: ApplicationRef
	) {
		this._logger = loggerService.createLogger('AppBootstrapService');
		patch(appRef);
	}

	run() {
		if (this._bootstrapped) throw new Error('duplicate call runAfterBootstrap method, this method must call only once');

		this._bootstrapped = true;
		Object.defineProperty(window, '__IS_1F_SPA_BOOT__', {
			get() {
				return true;
			},
		});

		// #region  logic after bootstrap
		this.tabActiveStateService.init();
		this.setupLocale();
		this.addAppVersionInfo();
		this.addSpecificCookies();
		this.runPlatformSpecificCode(this._platformDetector);
		this.prepareConfiguration();
		// this.checkAuthentication();
		this.registerCustomSvgIcons();
		this.registerFormaIcons();
		this.registerCustomElements(this.injector);

		initializeApi(this.injector);
		const disposeGlobalEventHandlers = initializeGlobalEventHandlers(this.injector);
		this.setupEvents();
		this.openNewsService.registerCommandOpenNews();
		this.openPortalService.registerCommandOpenPortal();
		// #endregion

		this._events.createAndSend(AppBootstrapEvent);

		this.removeMatIconsHackStyle();
		this.loadSessionUserSetting();
		this.updateSessionUserOnProfileChanged();
		this.checkMtfHasUnsavedChanges();
		this.updateTabsOnSignal();
	}

	loadSessionUserSetting() {
		this._auth.authenticated$.pipe(booleanFilter(), take(1)).subscribe(() => {
			this.sessionUserSettings.loadUserSettings();
		});
	}

	protected removeMatIconsHackStyle() {
		const removeHack = () => {
			try {
				document.querySelector('#mat-icons-initial-hack').remove();
			} catch (err) {
				console.error(err);
			}
		};
		let tries = 0;
		const fontsApi = (document as any)?.fonts;
		const isMatIconsLoaded = () => fontsApi.check(`1rem 'Material Icons'`);
		if (fontsApi && !isMatIconsLoaded()) {
			const interval = setInterval(() => {
				if (isMatIconsLoaded() || tries >= 40) {
					removeHack();
					clearInterval(interval);
				}
				tries++;
			}, 100);
		} else {
			removeHack();
		}
	}

	protected addSpecificCookies() {
		this._cookies.set(SystemCookies.forceHideHeaderAndTrees, '1', undefined, '/');
	}

	protected runPlatformSpecificCode(platformDetector: PlatformDetectorProvider) {
		if (!platformDetector) {
			return this._logger.warn(`platformDetector service is not defined'`);
		}
		if (platformDetector.isOneOfPlatform(platformDetector.types.android, platformDetector.types.ios)) {
			this._document.body.classList.add('is-mobile');
			// @see viewport on mobile https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
			// First we get the viewport height and we multiple it by 1% to get a value for a vh unit
			let vh = this._window.innerHeight * 0.01;
			// Then we set the value in the --vh custom property to the root of the document
			this._document.documentElement.style.setProperty('--vh', `${vh}px`);
			this.zone.runOutsideAngular(() => {
				this._window.addEventListener('resize', () => {
					// We execute the same script as before
					vh = this._window.innerHeight * 0.01;
					this._document.documentElement.style.setProperty('--vh', `${vh}px`);
				});
			});
		}

		const classes = platformDetector
			.detectPlatformTypes()
			.map(platform => platform.replace(' ', ''))
			.map(platform => `is-${platform.toLowerCase()}`);

		this._document.body.classList.add(...classes);
	}

	protected prepareConfiguration() {
		this._config.applyConfig({
			animation: !!this._animationModule,
		});
	}

	protected checkAuthentication() {
		this._auth.authenticated$.pipe(take(1)).subscribe(isAuth => {
			if (!isAuth && !this._auth.currentPageIsEntry) {
				this.zone.run(() => {
					this._auth.navigateToSignInPage(null, { initial: true }, true);
				});
			}
		});
	}

	protected addAppVersionInfo() {
		const pack: typeof packageJson = (<any>packageJson).default || <any>packageJson;
		this.versionService.update({
			name: pack.name,
			version: pack.version,
		});
	}

	protected registerFormaIcons() {
		formaIcons.forEach(icon => {
			this.iconRegistry.addSvgIcon(
				`vh-${icon.iconName}`,
				this.url.getSafeResourceUrl(
					this.url.getUrlRelativeToAssets(`icons/forma-icons/${icon.iconName}.svg?t=${this.ASSET_INVALIDATE_KEY}`)
				)
			);
		});
	}

	protected registerCustomSvgIcons() {
		this.iconRegistry.addSvgIcon(
			'pin',
			this.url.getSafeResourceUrl(
				this.url.getUrlRelativeToAssets(`icons/svg/pin-outline.svg?t=${this.ASSET_INVALIDATE_KEY}`)
			)
		);
		this.iconRegistry.addSvgIcon(
			'meeting',
			this.url.getSafeResourceUrl(
				this.url.getUrlRelativeToAssets(`icons/svg/meeting.svg?t=${this.ASSET_INVALIDATE_KEY}`)
			)
		);
		this.iconRegistry.addSvgIcon(
			'chat',
			this.url.getSafeResourceUrl(this.url.getUrlRelativeToAssets(`icons/svg/chat.svg?t=${this.ASSET_INVALIDATE_KEY}`))
		);
		this.iconRegistry.addSvgIcon(
			'star',
			this.url.getSafeResourceUrl(this.url.getUrlRelativeToAssets('icons/svg/star.svg'))
		);
	}

	protected setupLocale() {
		this.lazyLocaleNgModule
			.pipe(
				take(1),
				catchError(err => {
					console.error(err);
					return EMPTY;
				})
			)
			.subscribe(localeNg => {
				registerLocaleData(localeNg, this.localeId);
			});

		this._auth.authenticated$
			.pipe(
				distinctUntilChanged(),
				booleanFilter(),
				exhaustMap(() => this.culture.updateLanguageOnServer())
			)
			.subscribe();
	}

	protected setupEvents() {
		this._auth.authenticated$
			.pipe(
				booleanFilter(),
				take(1),
				concatMap(() => this.signal.activate())
			)
			.subscribe(() => {
				const se = (name: string) => `server-${name}`;

				this.signal.messages$.subscribe(({ data, name }) => {
					this.apiEvents.dispatchEvent(se(name), data);
					this.apiEvents.dispatchEvent(se('*'), data, se(name));
				});
			});
	}

	protected registerCustomElements(injector: Injector) {
		import('@spa/custom-elements').then(({ registerSpaCustomElements }) => {
			registerSpaCustomElements(injector);
		});
	}

	protected updateSessionUserOnProfileChanged() {
		this._auth.authenticated$
			.pipe(
				booleanFilter(),
				switchMap(_ => this.signal.userProfileChanged$)
			)
			.subscribe(_ => {
				this.zone.runTask(() => {
					this.sessionUser.update();
				});
			});
	}

	protected updateTabsOnSignal() {
		this._auth.authenticated$
			.pipe(
				booleanFilter(),
				switchMap(_ => {
					const stopImpersonation$ = this.signal.stopImpersonation$.pipe(
						mergeMap(() => this._auth.isReincarnateMode$),
						booleanFilter()
					);
					return merge(this.signal.changeLanguage$, this.signal.impersonate$, stopImpersonation$);
				}),
				concatMap(() => fromEvent(document, 'visibilitychange').pipe(take(1))),
				filter(() => document.visibilityState === 'visible')
			)
			.subscribe(_ => {
				this.zone.runTask(() => {
					window.location.reload();
				});
			});
	}

	protected checkMtfHasUnsavedChanges() {
		const mtfUnsavedChangesChecker = () => {
			const result$ = new Observable<boolean>(observer => {
				const mtfFrames = this.mtfFrame.findMtfIframes();
				if (!mtfFrames.length) {
					observer.next(false);
				} else {
					const isDirty = Boolean(mtfFrames.find(frame => this.mtfFrame.isMtfDirty(frame)));
					observer.next(isDirty);
				}
				observer.complete();
			});

			return result$;
		};
		this.unsavedChanges.addRouterChecker(mtfUnsavedChangesChecker);
	}
}
